import React, { forwardRef } from 'react';
import { cn } from '../../utils/cn';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  isLoading?: boolean;
  variant?: 'default' | 'filled' | 'outlined';
  size?: 'sm' | 'md' | 'lg';
}

const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helperText,
  leftIcon,
  rightIcon,
  isLoading,
  variant = 'default',
  size = 'md',
  className,
  id,
  disabled,
  ...props
}, ref) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

  const baseClasses = 'w-full border rounded-xl focus:outline-none transition-all duration-200 bg-white text-slate-900 placeholder-slate-400';
  
  const variantClasses = {
    default: 'border-slate-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-slate-300 focus:shadow-md',
    filled: 'border-transparent bg-slate-50 focus:ring-2 focus:ring-emerald-500 focus:bg-white hover:bg-slate-100',
    outlined: 'border-2 border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-slate-400'
  };

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-base',
    lg: 'px-5 py-4 text-lg'
  };

  const errorClasses = error
    ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50'
    : '';

  const disabledClasses = disabled
    ? 'opacity-50 cursor-not-allowed bg-slate-100'
    : '';

  const inputClasses = cn(
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    errorClasses,
    disabledClasses,
    leftIcon && 'pl-10',
    rightIcon && 'pr-10',
    className
  );

  return (
    <div className="space-y-2">
      {label && (
        <label
          htmlFor={inputId}
          className={cn(
            'block text-sm font-semibold transition-colors duration-200',
            error ? 'text-red-700' : 'text-slate-700',
            disabled && 'text-slate-400'
          )}
        >
          {label}
          {props.required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <div className={cn('text-slate-400', size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-lg' : 'text-base')}>
              {leftIcon}
            </div>
          </div>
        )}
        
        <input
          ref={ref}
          id={inputId}
          className={inputClasses}
          disabled={disabled || isLoading}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
          {...props}
        />
        
        {rightIcon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <div className={cn('text-slate-400', size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-lg' : 'text-base')}>
              {rightIcon}
            </div>
          </div>
        )}
        
        {isLoading && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-300 border-t-emerald-500"></div>
          </div>
        )}
      </div>
      
      {error && (
        <p 
          id={`${inputId}-error`}
          className="text-sm text-red-600 font-medium flex items-center gap-1"
          role="alert"
        >
          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}
      
      {helperText && !error && (
        <p 
          id={`${inputId}-helper`}
          className="text-sm text-slate-500"
        >
          {helperText}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
